/**
 * Sendeprofil: Warmup, Rate-Limits und Sendefenster.
 *
 * Reine Funktionen ueber uebergebenen Zaehlwerten — die Zaehlung selbst macht
 * der Worker per Abfrage. Dadurch ist die Logik ohne Datenbank testbar, und
 * genau hier lagen im Altsystem zwei Fehler:
 *
 *   1. Der Warmup-Tag ergab sich aus Kalendertagen seit Kampagnenstart. Eine
 *      Pause von einer Woche liess das Tageslimit weiterlaufen, als waere
 *      durchgehend gesendet worden. Hier zaehlt nur, an wie vielen Tagen
 *      tatsaechlich versendet wurde.
 *   2. Das Tageslimit wurde ueber ein rollierendes 24-Stunden-Fenster
 *      gemessen, die Verdopplung aber pro Kalendertag gerechnet. Beides muss
 *      denselben Tagesbegriff verwenden — hier den Kalendertag in der
 *      Zeitzone des Profils.
 */
/**
 * Profil fuer Kampagnen ohne eigenes Sendeprofil: keine Grenze, kein Warmup,
 * jeden Tag rund um die Uhr.
 *
 * Steht hier und nicht im Worker, weil auch die API sie braucht — sie
 * beantwortet damit die Frage "warum geht gerade nichts raus". Zwei
 * Vorgaben an zwei Orten waeren genau die Art Abweichung, die niemandem
 * auffaellt, bis die Oberflaeche etwas anderes behauptet als der Versand tut.
 */
export const DEFAULT_POLICY = {
    emailsPerHour: 0,
    emailsPerMinute: 0,
    emailsPerDay: 0,
    warmupEnabled: false,
    warmupStart: 50,
    warmupMultiplier: 1.4,
    warmupCap: 10_000,
    // Ein Fenster ueber alle Tage mit gleichem Anfang und Ende: ganzer Tag,
    // jeden Tag.
    sendWindows: [{ tage: [0, 1, 2, 3, 4, 5, 6], von: '00:00', bis: '00:00' }],
    blackoutDates: [],
    contactCooldownMinutes: 0,
    companyCooldownMinutes: 0,
    maxContactPerDay: 0,
    maxCompanyPerDay: 0,
    maxDomainPerHour: 0,
    timezone: 'Europe/Zurich',
};
/**
 * Tageslimit fuer den n-ten Sendetag.
 *
 * Tag 1 liefert warmupStart, danach wird je Sendetag multipliziert, begrenzt
 * durch warmupCap. Bei abgeschaltetem Warmup gibt es kein Tageslimit.
 */
export function warmupCapForDay(policy, sendingDay) {
    if (!policy.warmupEnabled)
        return Number.POSITIVE_INFINITY;
    const day = Math.max(1, Math.trunc(sendingDay));
    const raw = policy.warmupStart * policy.warmupMultiplier ** (day - 1);
    // Gerundet, nicht abgeschnitten: 50 * 1.4^2 ergibt in Gleitkomma
    // 97.99999999999999, und Math.floor machte daraus dauerhaft 97 statt 98.
    // Bei jedem Verdopplungsschritt fiele das Limit knapp unter den Wert, den
    // der Nutzer eingestellt hat.
    return Math.min(policy.warmupCap, Math.round(raw));
}
/**
 * Zwischengespeicherte Intl-Formatierer.
 *
 * Ein `new Intl.DateTimeFormat(...)` kostet ein Vielfaches des eigentlichen
 * Formatierens. Die Hochrechnung ruft diese Funktionen bis zu 525.600 Mal je
 * Durchlauf auf — ohne diesen Zwischenspeicher dauerte ein Durchlauf 15
 * Sekunden, und in Phase 2 haengt er an jedem Tastendruck.
 */
const zeitFormatierer = new Map();
const datumFormatierer = new Map();
function zeitFormat(timezone) {
    let vorhanden = zeitFormatierer.get(timezone);
    if (!vorhanden) {
        vorhanden = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        zeitFormatierer.set(timezone, vorhanden);
    }
    return vorhanden;
}
/** Zerlegt einen Zeitpunkt in Wochentag und Minute in der Zeitzone des Profils. */
export function localTimeIn(timezone, at) {
    const parts = zeitFormat(timezone).formatToParts(at);
    const lookup = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const weekdays = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
    };
    const hour = Number(lookup('hour'));
    const minute = Number(lookup('minute'));
    return {
        weekday: weekdays[lookup('weekday')] ?? 0,
        // Intl liefert Mitternacht je nach Umgebung als 24 statt 00.
        minutesOfDay: (hour % 24) * 60 + minute,
    };
}
function parseTime(value) {
    if (!value)
        return null;
    const match = /^(\d{1,2}):(\d{2})/.exec(value);
    if (!match)
        return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59)
        return null;
    return hours * 60 + minutes;
}
/** Kalendertag in der Zeitzone des Profils, als YYYY-MM-DD. */
export function localDateIn(timezone, at) {
    let vorhanden = datumFormatierer.get(timezone);
    if (!vorhanden) {
        // 'en-CA' liefert ISO-Form (2026-08-02) ohne eigenes Zusammensetzen.
        // Unbekannte Zeitzone: dieselbe Nachsicht wie in nextLocalSendTime — ein
        // Tippfehler in der Einstellung darf den Versand nicht abbrechen.
        const zone = isValidTimezone(timezone) ? timezone : 'Europe/Zurich';
        vorhanden = new Intl.DateTimeFormat('en-CA', {
            timeZone: zone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        datumFormatierer.set(timezone, vorhanden);
    }
    return vorhanden.format(at);
}
/** Liegt der Zeitpunkt auf einem eingetragenen Ausnahmetag? */
export function isBlackoutDate(policy, at) {
    if (policy.blackoutDates.length === 0)
        return false;
    return policy.blackoutDates.includes(localDateIn(policy.timezone, at));
}
/**
 * Deckt ein einzelnes Fenster diesen Zeitpunkt ab?
 *
 * Ein Fenster ueber Mitternacht (22:00 bis 06:00) wird unterstuetzt: liegt
 * das Ende vor dem Anfang, gilt die Vereinigung beider Teilstuecke.
 *
 * Unlesbare Zeitangaben geben den ganzen Tag frei, statt alles zu blockieren
 * — dieselbe Richtung wie in der Vorgaengerfassung. Ein kaputter Wert soll
 * einen Versand nicht heimlich stilllegen; er faellt beim Speichern in der
 * Zod-Pruefung auf, nicht erst hier.
 */
function deckt(window, minutesOfDay) {
    const from = parseTime(window.von);
    const to = parseTime(window.bis);
    if (from === null || to === null)
        return true;
    if (from === to)
        return true;
    if (from < to)
        return minutesOfDay >= from && minutesOfDay < to;
    return minutesOfDay >= from || minutesOfDay < to;
}
/**
 * Passt einer der Sendefenster auf diesen Zeitpunkt?
 *
 * Die beiden Ablehnungsgruende bleiben getrennt, weil sie fuer den Nutzer
 * verschiedene Saetze ergeben: "heute ist kein Sendetag" fuehrt zu "weiter am
 * Montag", "ausserhalb des Fensters" zu "weiter um 09:00". Ein gemeinsames
 * "blockiert" waere in der Oberflaeche wertlos.
 */
export function isWithinSendWindows(policy, at) {
    const { weekday, minutesOfDay } = localTimeIn(policy.timezone, at);
    const heute = policy.sendWindows.filter((w) => w.tage.includes(weekday));
    if (heute.length === 0)
        return { erlaubt: false, grund: 'outside_send_days' };
    if (heute.some((w) => deckt(w, minutesOfDay)))
        return { erlaubt: true };
    return { erlaubt: false, grund: 'outside_send_window' };
}
function totalWindowMinutesToday(policy, at) {
    const { weekday } = localTimeIn(policy.timezone, at);
    const windows = policy.sendWindows.filter((w) => w.tage.includes(weekday));
    if (windows.length === 0)
        return 245;
    let total = 0;
    for (const w of windows) {
        const from = parseTime(w.von);
        const to = parseTime(w.bis);
        if (from !== null && to !== null) {
            if (to > from)
                total += to - from;
            else if (to < from)
                total += 1440 - from + to;
            else
                total += 1440;
        }
    }
    return total > 0 ? total : 245;
}
/**
 * Wie viele Nachrichten jetzt gesendet werden duerfen.
 *
 * Es gewinnt die schaerfste Grenze. Der Grund wird mitgeliefert, damit im Log
 * und in der Oberflaeche steht, WARUM gerade nichts rausgeht — "Kampagne
 * haengt" ohne Begruendung ist der haeufigste Supportfall.
 */
export function allowanceNow(policy, counters, at = new Date()) {
    // Der Ausnahmetag zuerst: "24.12. ist als Ausnahmetag eingetragen" sagt
    // mehr als "heute ist kein Sendetag", und beides waere gleichzeitig wahr.
    if (isBlackoutDate(policy, at))
        return { allowed: 0, reason: 'blackout_date' };
    const fenster = isWithinSendWindows(policy, at);
    if (!fenster.erlaubt)
        return { allowed: 0, reason: fenster.grund };
    const limits = [];
    // Warmup-Deckel und Tageslimit ersetzen einander nicht — sie gelten
    // gleichzeitig, und unten gewinnt ohnehin die schaerfste Grenze. Beide
    // rechnen auf sentToday, also auf den Kalendertag in der Zeitzone des
    // Profils; ein rollierendes 24-Stunden-Fenster waere ein anderer
    // Tagesbegriff und genau der Fehler aus dem Kopfkommentar.
    if (policy.warmupEnabled) {
        const dailyCap = warmupCapForDay(policy, counters.sendingDay);
        limits.push({
            remaining: dailyCap - counters.sentToday,
            reason: 'warmup_cap',
        });
        // Intelligentes Warmup-Pacing ueber das Sendefenster (z. B. 07:45 - 11:50 = ca. 4 Stunden):
        // Verteilt die E-Mails natuerlich und gleichmaessig ueber den Vormittag.
        // Ein 20% Puffer stellt sicher, dass das Tagesziel vor Fensterschluss sicher erreicht wird.
        const windowMinutes = totalWindowMinutesToday(policy, at);
        const pacedPerHour = Math.max(15, Math.ceil((dailyCap / windowMinutes) * 60 * 1.2));
        const pacedPerMinute = Math.max(1, Math.ceil((dailyCap / windowMinutes) * 1.5));
        if (policy.emailsPerHour === 0) {
            limits.push({
                remaining: pacedPerHour - counters.sentLastHour,
                reason: 'hourly_limit',
            });
        }
        if (policy.emailsPerMinute === 0) {
            limits.push({
                remaining: pacedPerMinute - counters.sentLastMinute,
                reason: 'minute_limit',
            });
        }
    }
    if (policy.emailsPerDay > 0) {
        limits.push({
            remaining: policy.emailsPerDay - counters.sentToday,
            reason: 'daily_limit',
        });
    }
    if (policy.emailsPerHour > 0) {
        limits.push({
            remaining: policy.emailsPerHour - counters.sentLastHour,
            reason: 'hourly_limit',
        });
    }
    if (policy.emailsPerMinute > 0) {
        limits.push({
            remaining: policy.emailsPerMinute - counters.sentLastMinute,
            reason: 'minute_limit',
        });
    }
    if (limits.length === 0)
        return { allowed: Number.POSITIVE_INFINITY };
    const tightest = limits.reduce((a, b) => (a.remaining <= b.remaining ? a : b));
    if (tightest.remaining <= 0)
        return { allowed: 0, reason: tightest.reason };
    return { allowed: Math.floor(tightest.remaining) };
}
/** Verstaendliche Begruendung fuer Log und Oberflaeche. */
export function describeBlockReason(reason) {
    switch (reason) {
        case 'blackout_date':
            return 'Heute ist ein Ausnahmetag — an diesem Tag wird nicht versendet.';
        case 'outside_send_days':
            return 'Heute ist kein Sendetag laut Profil.';
        case 'outside_send_window':
            return 'Ausserhalb des eingestellten Sendefensters.';
        case 'warmup_cap':
            return 'Tageslimit des Warmups erreicht.';
        case 'daily_limit':
            return 'Tageslimit erreicht.';
        case 'hourly_limit':
            return 'Stundenlimit erreicht.';
        case 'minute_limit':
            return 'Minutenlimit erreicht.';
    }
}
/**
 * Naechster Zeitpunkt, an dem es in der angegebenen Zeitzone `hour:00` ist.
 *
 * Grundlage des Ortszeit-Versands (§8.1): "um 09:00 beim Empfaenger" heisst
 * fuer Zuerich, Tokio und New York drei verschiedene UTC-Zeitpunkte. Die
 * Suche laeuft ueber die Ortszeit selbst statt ueber einen festen Versatz —
 * feste Versaetze zerbrechen zweimal im Jahr an der Sommerzeit.
 *
 * Eine unbekannte Zeitzone faellt auf die Vorgabe zurueck, statt zu werfen:
 * Kontaktdaten kommen aus Importen, und "Europe/Zuerich" (Tippfehler) darf
 * den Kampagnenstart nicht abbrechen.
 */
export function nextLocalSendTime(timezone, hour, options = {}) {
    const now = options.now ?? new Date();
    const fallback = options.fallbackTimezone ?? 'Europe/Zurich';
    const zone = isValidTimezone(timezone) ? timezone : fallback;
    const target = Math.min(23, Math.max(0, Math.trunc(hour)));
    // Startpunkt: jetzt, auf die volle Stunde gerundet, plus grobe Differenz
    // zur Zielstunde in Ortszeit. Danach in Stundenschritten nachjustieren —
    // hoechstens zweimal noetig, an den Umstellungstagen der Sommerzeit.
    const local = localTimeIn(zone, now);
    const localHour = Math.floor(local.minutesOfDay / 60);
    let deltaHours = (target - localHour + 24) % 24;
    if (deltaHours === 0 && local.minutesOfDay % 60 > 0)
        deltaHours = 24;
    let candidate = new Date(now.getTime());
    candidate.setUTCMinutes(0, 0, 0);
    candidate = new Date(candidate.getTime() + deltaHours * 3_600_000);
    for (let i = 0; i < 3; i += 1) {
        const check = localTimeIn(zone, candidate);
        const checkHour = Math.floor(check.minutesOfDay / 60);
        if (checkHour === target)
            break;
        const correction = ((target - checkHour + 36) % 24) - 12;
        candidate = new Date(candidate.getTime() + correction * 3_600_000);
    }
    // Nie in der Vergangenheit: knapp verpasst heisst morgen.
    if (candidate.getTime() <= now.getTime()) {
        candidate = new Date(candidate.getTime() + 24 * 3_600_000);
    }
    return candidate;
}
export function isValidTimezone(timezone) {
    if (!timezone)
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone });
        return true;
    }
    catch {
        return false;
    }
}
/** Obergrenze der Simulation. Was danach nicht fertig ist, wird es nie. */
const VORSCHAU_MAX_TAGE = 365;
/**
 * Wann ist die Kampagne fertig?
 *
 * Die einzige Frage, die ein Nutzer beim Einstellen wirklich hat — und die
 * sieben Zahlenfelder allein nicht beantworten.
 *
 * Simuliert wird MINUTENWEISE ueber dieselbe Funktion, nach der auch der
 * Worker freigibt (allowanceNow). Das ist keine Ziererei: eine zweite,
 * aehnliche Rechnung im Browser wuerde frueher oder spaeter etwas anderes
 * sagen als der Versand tut, und dann verspricht die Oberflaeche eine Zeit,
 * die niemand haelt. Stundenschritte schieden aus, weil das Minutenlimit
 * darin nicht abbildbar ist: allowanceNow liefert eine Menge JE AUFRUF, und
 * "5 pro Minute" sind in einer Stunde 300, nicht 5.
 *
 * Zwei bewusste Ungenauigkeiten, damit sie niemand fuer Praezision haelt:
 *
 *   1. Der Worker laeuft alle 5 Sekunden in Stapeln, nicht im Minutentakt.
 *      Die Hochrechnung kann deshalb um einige Minuten danebenliegen —
 *      deshalb heisst es in der Oberflaeche "gegen 16:20" und nicht "16:20".
 *   2. Der Warmup-Sendetag steigt in der Wirklichkeit erst mit der ersten
 *      Nachricht des Tages (counters.ts zaehlt verschiedene Kalendertage MIT
 *      Versand). Die Simulation macht es genauso und faellt damit eher zu
 *      vorsichtig aus als zu optimistisch. Das ist die richtige Richtung:
 *      lieber frueher fertig als versprochen.
 */
export function versandVorschau(policy, empfaenger, options = {}) {
    const beginn = options.start ?? new Date();
    const senderLimit = options.senderTageslimit ?? 0;
    const hinweise = pruefeEinstellungen(policy, senderLimit);
    let rest = Math.max(0, Math.trunc(empfaenger) - Math.trunc(options.bereitsVersendet ?? 0));
    const leer = {
        start: null,
        fertig: null,
        dauerTage: 0,
        engsteGrenze: 'keine',
        proTag: [],
        hinweise,
    };
    if (rest === 0)
        return { ...leer, start: beginn, fertig: beginn };
    // Ohne Fenster geht nie etwas raus. Das faengt den haeufigsten "nie
    // fertig"-Fall ab, ohne 525.600 Minuten durchzurechnen.
    if (policy.sendWindows.length === 0) {
        return { ...leer, dauerTage: VORSCHAU_MAX_TAGE, engsteGrenze: 'outside_send_days' };
    }
    const proTagMap = new Map();
    const blockiert = new Map();
    /** Ringpuffer der letzten 60 Minuten fuer das Stundenlimit. */
    const letzteStunde = new Array(60).fill(0);
    let start = null;
    let fertig = null;
    let sendetage = 0;
    const schritte = VORSCHAU_MAX_TAGE * 24 * 60;
    for (let i = 0; i < schritte && rest > 0; i += 1) {
        const at = new Date(beginn.getTime() + i * 60_000);
        const datum = localDateIn(policy.timezone, at);
        const heuteSchon = proTagMap.get(datum) ?? 0;
        // Zuerst den Platz von vor 60 Minuten raeumen — er liegt jetzt
        // ausserhalb des rollierenden Stundenfensters. Erst danach summieren:
        // umgekehrt zaehlte die Stunde 61 Minuten.
        letzteStunde[i % 60] = 0;
        const counters = {
            sendingDay: Math.max(1, sendetage),
            sentToday: heuteSchon,
            sentLastHour: letzteStunde.reduce((a, b) => a + b, 0),
            sentLastMinute: 0,
        };
        const allowance = allowanceNow(policy, counters, at);
        if (allowance.allowed <= 0) {
            const grund = allowance.reason ?? 'keine';
            blockiert.set(grund, (blockiert.get(grund) ?? 0) + 1);
            /*
             * Ein Tag ohne Sendefenster und ein Ausnahmetag gelten fuer den GANZEN
             * lokalen Kalendertag — ihn Minute fuer Minute abzuschreiten kostet nur
             * Zeit. Der Sprung endet 60 Minuten VOR Mitternacht: an Tagen mit
             * Zeitumstellung hat der Kalendertag 23 statt 24 Stunden, und ein
             * Sprung auf die volle Tageslaenge wuerde dann in den naechsten Tag
             * hineinreichen und dort erlaubte Minuten verschlucken.
             *
             * Die Zaehlung in `blockiert` wird dadurch fuer diese beiden Gruende
             * unvollstaendig. Das ist ohne Folge: engsteGrenze wertet ohnehin nur
             * Mengengrenzen aus (siehe MENGENGRENZEN).
             */
            if (grund === 'outside_send_days' || grund === 'blackout_date') {
                const bisMitternacht = 1440 - localTimeIn(policy.timezone, at).minutesOfDay;
                i += Math.max(0, bisMitternacht - 61);
            }
            continue;
        }
        // Das Tageslimit des Absenders steht ausserhalb des Sendeprofils; der
        // Worker prueft es getrennt (senders.ts). Fuer die Hochrechnung muss es
        // trotzdem gelten, sonst verspricht sie eine Menge, die der Absender
        // gar nicht hergibt.
        const senderRest = senderLimit > 0 ? senderLimit - heuteSchon : Number.POSITIVE_INFINITY;
        if (senderRest <= 0) {
            blockiert.set('sender_tageslimit', (blockiert.get('sender_tageslimit') ?? 0) + 1);
            continue;
        }
        const menge = Math.min(allowance.allowed, rest, senderRest);
        if (menge <= 0)
            continue;
        if (heuteSchon === 0)
            sendetage += 1;
        proTagMap.set(datum, heuteSchon + menge);
        letzteStunde[i % 60] = menge;
        rest -= menge;
        start ??= at;
        if (rest === 0)
            fertig = at;
    }
    const engsteGrenze = groessterEintrag(blockiert);
    const dauerTage = fertig && start ? kalendertage(policy.timezone, start, fertig) : VORSCHAU_MAX_TAGE;
    // Dieser Hinweis braucht die Laufzeit und kann deshalb erst hier entstehen.
    const abstand = policy.contactCooldownMinutes ?? 0;
    if (abstand > 0 && dauerTage <= 1 && fertig !== null) {
        hinweise.push(`Abstand je Kontakt ${Math.round(abstand / 60)} Stunden, aber die Kampagne läuft nur einen Tag — der Abstand wirkt hier nicht.`);
    }
    return {
        start,
        fertig,
        dauerTage,
        engsteGrenze,
        proTag: [...proTagMap.entries()].slice(0, 30).map(([datum, menge]) => ({ datum, menge })),
        hinweise,
    };
}
/**
 * Nur Mengengrenzen zaehlen als "engste Grenze".
 *
 * Sendefenster und Ausnahmetage blockieren zwangslaeufig die meisten Minuten
 * einer Woche — naehme man sie mit, gewaenne immer das Fenster, und die
 * Anzeige waere wertlos. Gefragt ist, WELCHE MENGE bremst; wann ueberhaupt
 * gesendet werden darf, steht ohnehin daneben.
 */
const MENGENGRENZEN = new Set([
    'warmup_cap',
    'daily_limit',
    'hourly_limit',
    'minute_limit',
    'sender_tageslimit',
]);
function groessterEintrag(zaehler) {
    let beste = 'keine';
    let hoechste = 0;
    for (const [grund, anzahl] of zaehler) {
        if (!MENGENGRENZEN.has(grund))
            continue;
        if (anzahl > hoechste) {
            hoechste = anzahl;
            beste = grund;
        }
    }
    return beste;
}
function kalendertage(timezone, von, bis) {
    const a = new Date(`${localDateIn(timezone, von)}T00:00:00Z`).getTime();
    const b = new Date(`${localDateIn(timezone, bis)}T00:00:00Z`).getTime();
    return Math.round((b - a) / 86_400_000) + 1;
}
/**
 * Fenster in ein Raster aus sieben Tagen mal 24 Stunden.
 *
 * Grundlage der Rasterbedienung: man malt seine Sendezeiten, statt vier
 * Zahlenfelder auszufuellen. Eine Stunde gilt als gedeckt, wenn ihre erste
 * Minute gedeckt ist — das Raster kennt keine Viertelstunden.
 *
 * `raster[tag][stunde]`, tag 0 = Sonntag wie ueberall sonst hier.
 */
export function rasterAusFenstern(fenster) {
    return Array.from({ length: 7 }, (_, tag) => Array.from({ length: 24 }, (_, stunde) => fenster.some((f) => f.tage.includes(tag) && deckt(f, stunde * 60))));
}
/**
 * Raster zurueck in Fenster.
 *
 * Zusammenhaengende Stunden werden zu einem Fenster, und Wochentage mit
 * gleichem Muster teilen sich eines — sonst entstuenden aus "Mo-Fr 9-17"
 * fuenf Eintraege statt einem, und die Textfassung waere unlesbar.
 *
 * Ein ganzer Tag wird zu `von === bis`, weil '24:00' keine gueltige Uhrzeit
 * ist. Ein Block bis Mitternacht endet auf '00:00' — `deckt` liest das als
 * "bis Tagesende" (from > to), was hier genau stimmt.
 */
export function fensterAusRaster(raster) {
    /** Je Tag die Liste seiner Bloecke, als Text zum Vergleichen. */
    const bloeckeJeTag = raster.map((stunden) => {
        const bloecke = [];
        let start = null;
        for (let stunde = 0; stunde < 24; stunde += 1) {
            if (stunden[stunde] && start === null)
                start = stunde;
            if (!stunden[stunde] && start !== null) {
                bloecke.push([start, stunde]);
                start = null;
            }
        }
        if (start !== null)
            bloecke.push([start, 24]);
        return bloecke;
    });
    // Tage mit identischem Muster zusammenfassen.
    const nachMuster = new Map();
    bloeckeJeTag.forEach((bloecke, tag) => {
        if (bloecke.length === 0)
            return;
        const schluessel = JSON.stringify(bloecke);
        const vorhanden = nachMuster.get(schluessel);
        if (vorhanden)
            vorhanden.push(tag);
        else
            nachMuster.set(schluessel, [tag]);
    });
    const fenster = [];
    for (const [schluessel, tage] of nachMuster) {
        const bloecke = JSON.parse(schluessel);
        for (const [von, bis] of bloecke) {
            fenster.push({
                tage,
                von: alsUhrzeit(von),
                // Ein voller Tag und ein Block bis Mitternacht enden beide auf
                // '00:00'; bei 0..24 ist das zugleich der Ganztagsfall.
                bis: alsUhrzeit(bis % 24),
            });
        }
    }
    return fenster;
}
function alsUhrzeit(stunde) {
    return `${String(stunde).padStart(2, '0')}:00`;
}
/**
 * Verliert das Raster Angaben dieses Profils?
 *
 * Fenster koennen Minuten tragen (09:30), das Raster kann es nicht. Wer ein
 * solches Profil im Raster bearbeitet, rundet es auf volle Stunden — das muss
 * er vorher wissen, statt es hinterher zu entdecken.
 */
export function rasterVerliertMinuten(fenster) {
    return fenster.some((f) => !f.von.endsWith(':00') || !f.bis.endsWith(':00'));
}
/** Wie viele Minuten eines Wochentags von mindestens einem Fenster gedeckt sind. */
export function abgedeckteMinuten(policy, weekday) {
    const gedeckt = new Array(1440).fill(false);
    for (const fenster of policy.sendWindows) {
        if (!fenster.tage.includes(weekday))
            continue;
        for (let m = 0; m < 1440; m += 1) {
            if (deckt(fenster, m))
                gedeckt[m] = true;
        }
    }
    return gedeckt.filter(Boolean).length;
}
/**
 * Einstellungen, die einander aufheben.
 *
 * Der haeufigste Supportfall ist nicht "es geht nichts raus", sondern "es
 * geht weniger raus, als ich eingestellt habe" — weil eine zweite Grenze
 * frueher greift als die, an der man gedreht hat. Diese Hinweise sagen es
 * vorher statt hinterher.
 */
function pruefeEinstellungen(policy, senderTageslimit) {
    const hinweise = [];
    const tageMitFenster = new Set(policy.sendWindows.flatMap((w) => w.tage));
    if (tageMitFenster.size === 0) {
        hinweise.push('Kein Sendetag ausgewählt — mit diesem Profil geht nie etwas raus.');
        return hinweise;
    }
    const maxMinuten = Math.max(...[...tageMitFenster].map((tag) => abgedeckteMinuten(policy, tag)));
    const fensterStunden = maxMinuten / 60;
    const tagesdeckel = Math.min(policy.emailsPerDay > 0 ? policy.emailsPerDay : Number.POSITIVE_INFINITY, policy.warmupEnabled ? warmupCapForDay(policy, 1) : Number.POSITIVE_INFINITY);
    if (policy.emailsPerHour > 0 && Number.isFinite(tagesdeckel)) {
        const moeglich = policy.emailsPerHour * fensterStunden;
        if (moeglich > tagesdeckel) {
            const quelle = policy.warmupEnabled && warmupCapForDay(policy, 1) <= (policy.emailsPerDay || Number.POSITIVE_INFINITY)
                ? `der Warmup-Deckel liegt am ersten Sendetag bei ${warmupCapForDay(policy, 1)}`
                : `das Tageslimit liegt bei ${policy.emailsPerDay}`;
            hinweise.push(`Stundenlimit ${policy.emailsPerHour} greift nie: das Sendefenster ist ${fensterStunden.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Stunden lang und ${quelle}.`);
        }
    }
    if (policy.emailsPerMinute > 0 && policy.emailsPerHour > 0) {
        if (policy.emailsPerMinute * 60 <= policy.emailsPerHour) {
            hinweise.push(`Stundenlimit ${policy.emailsPerHour} greift nie: ${policy.emailsPerMinute} pro Minute ergeben höchstens ${policy.emailsPerMinute * 60} pro Stunde.`);
        }
    }
    if (senderTageslimit > 0 && policy.emailsPerDay > senderTageslimit) {
        hinweise.push(`Tageslimit ${policy.emailsPerDay}, aber das Tageslimit des Absenders liegt bei ${senderTageslimit} — es gewinnt der Absender.`);
    }
    return hinweise;
}
/**
 * Smart Sending / Frequency Capping (inspiriert von Klaviyo.com).
 *
 * Prüft, ob ein Kontakt innerhalb des Mindestabstands (z. B. 16 Stunden)
 * bereits eine E-Mail erhalten hat.
 */
export function isFrequencyCapped(lastSentAt, minIntervalHours = 16, now = new Date()) {
    if (!lastSentAt)
        return false;
    const elapsedMs = now.getTime() - lastSentAt.getTime();
    return elapsedMs < minIntervalHours * 3_600_000;
}
//# sourceMappingURL=policy.js.map